'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsSurfaceTypeSectionDisplayed from '@alltrails/amplitude/events/logTrailDetailsSurfaceTypeSectionDisplayed';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import Typography from '@alltrails/core/components/Typography';
import Button from '@alltrails/denali/components/Button';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Hide from '@alltrails/denali/icons/Hide';
import Showing from '@alltrails/denali/icons/Showing';
import useLogWebSectionViewed from '@alltrails/layout/hooks/useLogWebSectionViewed';
import SurfaceTypeBreakdown from '@alltrails/maps/components/SurfaceTypeBreakdown/SurfaceTypeBreakdown';
import { useGetTrailSurfaceTypesQuery } from '@alltrails/maps/redux/mapsApi';
import { updateDynamicStyleIds } from '@alltrails/maps/redux/reducer';
import TrailSurfaceTypesData from '@alltrails/maps/types/TrailSurfaceTypesData';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setIsTrailMapOpen } from '@/utils/trailPageSlice';
import updateSurfaceTypeIsHidden from '@/utils/updateSurfaceTypeIsHidden';
import PageSection from '../layouts/PageSection';
import styles from './SurfaceType.module.scss';

type SurfaceTypeSectionProps = {
  data: TrailSurfaceTypesData;
  isTrailAdmin: boolean;
  trailId: number;
};

// This is split into its own component so that useLogWebSectionViewed isn't called until we know that the section will be shown, and the ref will be set
const SurfaceTypeSection = ({ data, isTrailAdmin, trailId }: SurfaceTypeSectionProps) => {
  const isMobile = useIsMobileSizedScreen();
  const dispatch = useAppDispatch();
  const { displayMetric, dynamicStyleIds } = useAppSelector(state => ({
    displayMetric: state.context.displayMetric,
    dynamicStyleIds: state.map.dynamicStyleIds
  }));

  const ref = useRef<HTMLDivElement>(null);
  useLogWebSectionViewed(ref, 'Surface Type', 'trail page');

  const [isSurfaceTypeHidden, setIsSurfaceTypeHidden] = useState(false);

  useEffect(() => {
    setIsSurfaceTypeHidden(data.hiddenFromUser || false);
  }, [data]);

  const showOnMapButton = (
    <Button
      onClick={() => {
        logTrailDetailsActionTapped({
          trail_id: String(trailId),
          trail_page_action: 'surface type show as map tapped',
          trail_page_location: 'surface type',
          trail_page_type: 'trail detail page'
        });
        dispatch(setIsTrailMapOpen(true));
        if (!dynamicStyleIds.includes('surfaceType')) {
          dispatch(updateDynamicStyleIds([...dynamicStyleIds, 'surfaceType']));
        }
      }}
      text={<FormattedMessage defaultMessage="Show on map" />}
      width="fullOnMobile"
    />
  );

  const getTrailAdminButton = () => {
    if (!isTrailAdmin) {
      return null;
    }

    const getOnClick = (shouldHide: boolean) => () => {
      updateSurfaceTypeIsHidden(trailId, shouldHide)
        .then(({ hiddenFromUser }) => {
          setIsSurfaceTypeHidden(hiddenFromUser);
        })
        .catch(error => {
          console.log(`Failed to update surface type visibility: ${error}`);
        });
    };

    if (isSurfaceTypeHidden) {
      return (
        <Button
          icon={{ Component: Showing }}
          onClick={getOnClick(false)}
          text={<FormattedMessage defaultMessage="Show this module" />}
          variant="primary"
        />
      );
    }
    return (
      <Button
        icon={{ Component: Hide }}
        onClick={getOnClick(true)}
        text={<FormattedMessage defaultMessage="Hide this module" />}
        variant="elevated"
      />
    );
  };

  return (
    <PageSection>
      <div className={styles.container} ref={ref}>
        <div className={styles.title}>
          <Typography variant="heading400">
            <FormattedMessage defaultMessage="Surface type" />
          </Typography>
          {isMobile ? null : (
            <div className={styles.buttons}>
              {showOnMapButton}
              {getTrailAdminButton()}
            </div>
          )}
        </div>
        <SurfaceTypeBreakdown
          className={!isMobile && isTrailAdmin && isSurfaceTypeHidden ? styles.hiddenBreakdown : undefined}
          showVisualBreakdown
          surfaceTypeAggregation={data!.surfaceTypes.aggregation}
          verticalSpacing="compact"
          useMetric={displayMetric}
        />
        {isMobile ? <div className={styles.buttons}>{showOnMapButton}</div> : null}
      </div>
    </PageSection>
  );
};

type SurfaceTypeProps = {
  trailId: number;
};

const SurfaceType = ({ trailId }: SurfaceTypeProps) => {
  const { hasPermission } = useAuthorization();
  const isTrailAdmin = hasPermission({ permission: 'trails:manage' });

  const { data, isError } = useGetTrailSurfaceTypesQuery({
    includeSegments: false,
    showHidden: isTrailAdmin,
    trailId
  });

  const shouldHideSurfaceType = useMemo(() => {
    if (isError || !data) {
      return true;
    }

    // Trail admins can always see the surface type module
    if (isTrailAdmin) {
      return false;
    }

    if (data.hiddenFromUser) {
      return true;
    }

    const surfaceTypeAggregation = data.surfaceTypes.aggregation;
    if ((surfaceTypeAggregation.find(({ surfaceType }) => surfaceType.identifier === 'unknown')?.percentOfSurface || 0) > 50) {
      // Don't show the surface type module when the unknown surface type is more than 50% of the trail
      return true;
    }
    return false;
  }, [isError, data, isTrailAdmin]);

  useEffect(() => {
    // Wait for the data to be fetched in order to determine if the surface type section will be shown
    if (data && !isError) {
      logTrailDetailsSurfaceTypeSectionDisplayed({ is_surface_type_shown: !shouldHideSurfaceType, trail_id: String(trailId) });
    }
  }, [isError, data, shouldHideSurfaceType, trailId]);

  if (shouldHideSurfaceType) {
    // Most trails don't have enough known surface type to display this module,
    // so we don't show an error state since the likely result would be that even if
    // a subsequent request succeeds, the module would still be hidden.
    return null;
  }

  return <SurfaceTypeSection data={data!} isTrailAdmin={isTrailAdmin} trailId={trailId} />;
};

export default SurfaceType;
